import type {
  Step,
  ServiceCategoryItem,
  PricingRow,
  InfoSection,
  FAQ,
  KeywordContent,
} from "../../types";

// ---------------------------------------------------------------------------
// English content for the /en/junk-removal page
// ---------------------------------------------------------------------------

const steps: Step[] = [
  {
    number: 1,
    title: "Book your junk removal",
    description:
      "Enter your address, fill in the details, and pick a time that works for you. You'll see the price and can book instantly online.",
  },
  {
    number: 2,
    title: "We pick up and haul everything away",
    description:
      "Our young workers come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, and narrow elevators - you don't have to lift a finger.",
  },
  {
    number: 3,
    title: "All cleared out",
    description:
      "We drive everything to the nearest waste station or recycling center. You don't have to worry about sorting or transport.",
  },
];

const categories: ServiceCategoryItem[] = [
  {
    image: "/recycling-bin.webp",
    name: "Recycling",
    desc: "Glass, metal, paper, and other recyclables",
  },
  {
    image: "/box.webp",
    name: "Cardboard",
    desc: "Moving boxes and corrugated cardboard",
  },
  {
    image: "/furniture.webp",
    name: "Furniture",
    desc: "Living room, bedroom, office, and other household furniture",
  },
  {
    image: "/electronics.webp",
    name: "Electronics",
    desc: "TVs, computers, audio systems, and other electronics",
  },
  {
    image: "/appliances.webp",
    name: "Appliances",
    desc: "Refrigerators, washing machines, dryers, and other appliances",
  },
  {
    image: "/fitness-equipment.webp",
    name: "Fitness Equipment",
    desc: "Treadmills, bikes, weights, and other workout equipment",
  },
];

const pricing: PricingRow[] = [
  {
    name: "Small",
    desc: "1–2 larger items like a sofa or fridge",
    image: "/service-things-small.webp",
    crew: "2 people + van",
    framkorning: "First 20 km included, then SEK 8/km",
    avgCost: "SEK 995",
  },
  {
    name: "Medium",
    desc: "Multiple pieces of furniture or bulky items",
    image: "/service-room-medium.webp",
    crew: "2 people + van",
    framkorning: "First 20 km included, then SEK 12/km",
    avgCost: "SEK 1,495",
  },
  {
    name: "Large",
    desc: "Clearing a room or small apartment",
    image: "/service-space-large.webp",
    crew: "2 people + van",
    framkorning: "First 20 km included, then SEK 15/km",
    avgCost: "SEK 2,495",
  },
  {
    name: "X-Large",
    desc: "Complete clearance of a home or office",
    image: "/service-whole-place-x-large.webp",
    crew: "2 people + van",
    framkorning: "First 20 km included, then SEK 18/km",
    avgCost: "SEK 4,995",
  },
];

const infoSections: InfoSection[] = [
  {
    heading: "Junk Removal",
    content:
      "Getting rid of bulky furniture, electronics, or an entire household doesn't have to be complicated. With Ungdomskraft you get fast, hassle-free junk removal - whether it's a single sofa, a full estate clearance, or a storage unit that needs emptying. We take care of everything from heavy lifting to responsible sorting and recycling, so you never have to drive to the dump yourself.\n\nOur young, energetic workers pick up your junk directly from your home, office, or storage unit. Just point at what needs to go - we carry, load, and haul it all away for you.",
  },
  {
    heading: "Why book junk removal with Ungdomskraft?",
    listItems: [
      {
        label: "Simple booking, no hassle -",
        text: "Book your junk removal in minutes. Enter your address, pick a date, and get an instant price - no waiting for quotes. Need help today? We can be there within 60 minutes for urgent requests. You can also call us at +46 8-517 082 81.",
      },
      {
        label: "Flexible times that suit you -",
        text: "We know your time is valuable. That's why we offer junk removal at times that fit your schedule - weekdays, evenings, and weekends. Choose the date and time that works best for you.",
      },
      {
        label: "Eco-friendly handling and recycling -",
        text: "We sort and recycle as much as possible. Items in good condition are donated to charity organizations. The rest is handled responsibly and taken to approved recycling stations. You may also be eligible for the Swedish RUT tax deduction when we transport items for reuse.",
      },
      {
        label: "Insured and secure -",
        text: "Every job is insured through Trygg-Hansa. You can feel confident that your belongings and property are protected throughout the entire process - from lifting to transport.",
      },
    ],
  },
  {
    heading: "What junk can we remove?",
    content:
      "We handle most types of bulky items and waste. Whether you need to get rid of a few things or clear out an entire space, we're here to help:",
    listItems: [
      { label: "Furniture -", text: "sofas, beds, tables, chairs, bookshelves, and wardrobes" },
      { label: "Appliances -", text: "washing machines, dryers, refrigerators, freezers, and stoves" },
      { label: "Electronics -", text: "TVs, computers, monitors, and smaller e-waste" },
      { label: "Bulk waste -", text: "mattresses, rugs, lamps, and other items that don't fit in regular bins" },
      { label: "Garden waste -", text: "branches, soil, stones, and other garden debris" },
      { label: "Construction debris -", text: "demolition materials, drywall, and waste from minor renovations" },
      { label: "Office equipment -", text: "desks, office chairs, filing cabinets, and IT equipment" },
    ],
    afterContent:
      "We also accept mixed loads where everything needs to go at once. Not sure if we can handle your particular waste? Get in touch and we'll help you out.",
  },
  {
    heading: "How it works - junk removal in three easy steps",
    listItems: [
      {
        label: "1. Describe what you need -",
        text: "Fill in our booking form with your address, what needs to be removed, and your preferred date. You'll get an instant price - no hidden costs.",
      },
      {
        label: "2. We come to you -",
        text: "Our young workers show up at the agreed time, carry everything out, and load up the truck. We handle staircases, elevators, and tight spaces - you don't have to lift a finger.",
      },
      {
        label: "3. Responsible disposal -",
        text: "We take your waste to the right place. Items that can be reused are donated; the rest is sorted and delivered to an approved recycling station. Everything is handled according to regulations so as little as possible ends up in landfill.",
      },
    ],
  },
  {
    heading: "Experienced junk haulers with local knowledge",
    content:
      "Our young workers have extensive experience with junk removal. They are used to tight staircases, narrow elevators, and courtyards that require extra planning. Whether you live in a house, an apartment, or a townhouse, they know how to get the job done efficiently and safely.\n\nWe carry out daily junk removals - from single pieces of furniture to complete apartment clearances and estate clear-outs. That experience means we can give you an accurate price at the time of booking and complete the job without delays.",
  },
  {
    heading: "More than just junk removal - you're making a difference",
    content:
      "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book junk removal with us, you don't just get professional help picking up junk and driving to the dump - you also help young people enter the workforce and build their future.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. It's junk removal that makes a difference - every booking counts.",
  },
  {
    heading: "Book junk removal today",
    content:
      "Looking for junk removal near me and want a service that combines convenience, reliability, and great value? Then Ungdomskraft is the right choice. We make it easy to get rid of what you no longer need - fast, safe, and eco-friendly.\n\nFill in the form and get an instant price. No quote requests - just simple junk removal that also makes a difference. You can also call us to book at +46 8-517 082 81.",
    cta: { text: "Book junk removal", href: "#priser" },
  },
];

const faqs: FAQ[] = [
  {
    q: "What types of items can you haul away?",
    a: "We can remove almost anything - from old furniture, mattresses, and appliances to cardboard boxes, garden waste, and general household junk. We also handle bulky items like sofas, bookshelves, and fitness equipment. Whether it's a garage cleanout, office clearance, or a full apartment clear-out, our young workers make the junk removal quick and easy. For safety reasons, we cannot handle hazardous waste such as chemicals, paint, or asbestos.",
  },
  {
    q: "Can you pick up items from inside my home, or do they need to be outside?",
    a: "Yes, our workers will pick up your items wherever they are - inside your home, in the attic, basement, or garage. You don't need to carry anything out yourself. We're experienced with tight staircases, narrow elevators, and hard-to-reach spaces.",
  },
  {
    q: "What happens to my items after the junk removal?",
    a: "We sort everything we haul away. Items still in good condition are donated to charity organizations. The rest is taken to an approved recycling station where materials are sorted and recycled according to environmental regulations. Our goal is always to send as little as possible to landfill.",
  },
  {
    q: "Can you remove mattresses, electronics, or appliances?",
    a: "Absolutely. We pick up and haul away mattresses, TVs, computers, washing machines, refrigerators, freezers, and other appliances. Electronics and appliances are separated and taken to approved e-waste recycling. Additional disposal fees at the recycling station may apply depending on the item type.",
  },
  {
    q: "How should I prepare for my junk removal?",
    a: "You really don't need to do much at all - our workers handle the heavy lifting. But if you want to speed things up, you can clear a path (about 1 meter wide) from the entrance to the items, and if possible, gather them in one place. This reduces work time and keeps the cost down.",
  },
  {
    q: "Do you offer same-day or next-day junk removal?",
    a: "Yes, we offer both same-day and next-day service. You can book our workers with as little as 30 minutes' notice, or plan up to 30 days in advance. Simply choose the time that works best for you in the booking form.",
  },
  {
    q: "Can you disassemble furniture during the junk removal?",
    a: "Yes, our workers are happy to help with basic disassembly as part of your junk removal. They carry standard tools, but if your furniture requires special tools or instructions, please have them ready. Time for disassembly is included in your total work time.",
  },
  {
    q: "Can you donate my items instead of hauling them to the dump?",
    a: "Yes! If your items are still in good condition, we can deliver them to a secondhand store or charity organization instead of the recycling station. Just specify the preferred drop-off location when booking. We recommend contacting the recipient in advance to confirm they can accept your items. Transport for reuse may also qualify you for the Swedish RUT tax deduction (25%).",
  },
  {
    q: "Are disposal fees included in the price?",
    a: "No, waste and disposal fees are not included in the price and are charged separately. The fee varies depending on the recycling station and the weight and type of items, but is usually around 400 kr. You'll always see the total cost before we start the job, so you know exactly what to expect.",
  },
  {
    q: "Can I get a RUT tax deduction on junk removal?",
    a: "It depends on what happens to the items. If your items are transported to a secondhand shop, charity, or another location for reuse, you're eligible for the RUT tax deduction on the labor cost. Regular junk removal where items go directly to a recycling station or landfill does not qualify for the RUT deduction under Swedish tax rules. We're happy to help you figure out what applies to your specific job - just let us know at booking if you'd like items donated and we'll plan accordingly. The deduction is applied directly on the invoice, so you never have to front the money.",
  },
  {
    q: "How much does junk removal cost?",
    a: "The price depends on the volume, type of items, and how long the job takes. You get a price instantly in our booking system based on what you enter - no hidden fees or surprises. Disposal fees at the recycling station are charged separately and usually run around 400 kr. Want a precise estimate for a larger job? Contact us and we'll get back to you with a tailored quote.",
  },
  {
    q: "How long does a junk removal take?",
    a: "It varies depending on the volume and how accessible the items are. A simple removal of a couple of furniture pieces usually takes 30–60 minutes. Larger jobs like an apartment clearance or estate clear-out can take half a day to a full day. Our young workers are efficient and used to heavy lifting - we make sure it goes as quickly as possible without compromising quality.",
  },
  {
    q: "Can you help with estate clearance or apartment clear-outs?",
    a: "Yes, we have extensive experience clearing estates and entire apartments. Our workers handle everything from furniture and personal belongings to bulk waste and electronics. We sort carefully - items still in good condition are donated and the rest is taken to an approved recycling station. You don't need to be present if you don't want to - we'll manage the entire process for you.",
  },
  {
    q: "Which areas do you offer junk removal in?",
    a: "We offer junk removal across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö, and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn, and Öckerö. Don't see your area? Get in touch and we'll let you know if we can help.",
  },
  {
    q: "Who arranges parking for the truck?",
    a: "We take care of that. Our workers are experienced at finding parking solutions in tight residential areas and inner-city streets. If special parking permits or access are required, we'll let you know in advance. You don't need to worry about the logistics - it's all part of the service.",
  },
];

// ---------------------------------------------------------------------------
// Exported keyword content
// ---------------------------------------------------------------------------

export const junkRemoval: KeywordContent = {
  slug: "junk-removal",
  keyword: "Junk Removal",
  headingPrefix: "Help with",
  headingKeyword: "junk removal {{kommun}}",
  subtext:
    "Book junk removal {{kommun}} of furniture, appliances, and bulky items - fast and easy. We send young workers with a truck straight to your address, often the same day. Fixed price, simple booking, and over 50,000 completed jobs.",
  heroImage: "/hero-bortforsling.webp",
  featureCardsHeading: "We handle your junk removal {{kommun}}",
  howItWorksHeading: "Book your junk removal {{kommun}} in 3 easy steps",
  howItWorksSteps: steps,
  serviceCategoriesHeading: "Junk we remove {{kommun}}",
  serviceCategoriesItems: categories,
  valuePropHeading: "Junk removal {{kommun}} pricing",
  valuePropRows: pricing,
  priceEstimateSubtitle:
    "Enter your addresses to see prices and book your junk removal {{kommun}}",
  availableAreasHeading: "Junk removal available in these municipalities",
  serviceInfoHeading: "Junk Removal {{kommun}}",
  serviceInfoSections: infoSections,
  serviceFAQHeading: "Frequently Asked Questions - Junk Removal {{kommun}}",
  serviceFAQItems: faqs,
  metaTitle: "Junk Removal {{kommun}} | Ungdomskraft",
  metaDescription:
    "Book junk removal {{kommun}} of furniture, appliances, and bulky items. Fixed price, easy booking. Ungdomskraft - over 50,000 completed jobs.",
};
